import { memo, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { TableVirtuoso, TableVirtuosoProps, TableVirtuosoHandle } from 'react-virtuoso';

import { Table } from '@tanstack/react-table';

const wrapperClassName = 'virtualize-wrapper';
export const scrolledClassName = 'scrolled';

type VirtualTableProps = TableVirtuosoProps<any, any> & {
  isResizing: boolean;
  table: Table<any>;
  resetScrollOnDataChange?: boolean;
};

export type VirtualTableHandle = {
  scrollToTop(): void;
};

export const VirtualTable = memo(
  forwardRef<VirtualTableHandle, VirtualTableProps>(
    ({ isResizing, table, resetScrollOnDataChange, ...rest }, ref) => {
      const virtuosoRef = useRef<TableVirtuosoHandle>(null);

      useImperativeHandle(ref, () => ({
        scrollToTop: () => {
          virtuosoRef.current?.scrollToIndex({ index: 0, behavior: 'auto' });
        },
      }));

      // When the wrapper is scrolled, we add a className for the table styling to use
      useEffect(() => {
        const scrollElement = document.getElementsByClassName(wrapperClassName)?.[0] ?? window;
        const handleScroll = () => {
          if (scrollElement.scrollLeft > 0) {
            scrollElement.classList.add(scrolledClassName);
          } else {
            scrollElement.classList.remove(scrolledClassName);
          }
        };

        scrollElement.addEventListener('scroll', handleScroll);

        return () => {
          scrollElement.removeEventListener('scroll', handleScroll);
        };
      }, []);

      // Reset scroll position when data changes (e.g., page change)
      useEffect(() => {
        if (resetScrollOnDataChange) {
          virtuosoRef.current?.scrollToIndex({ index: 0, behavior: 'auto' });
        }
      }, [table.options.data, resetScrollOnDataChange]);

      return <TableVirtuoso ref={virtuosoRef} className={wrapperClassName} {...rest} />;
    },
  ),
  // We want to return a memoized table when we are resizing, and table data has not changed
  ({ table: oldTable }, { table: newTable, isResizing }) =>
    isResizing && oldTable.options.data === newTable.options.data,
);
